import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    Switch,
    FormControlLabel,
    Typography,
    Chip,
    CircularProgress,
    Alert,
} from '@mui/material';
import { ReferralLink, createReferralLinkRequest, updateReferralLinkRequest } from '../api/referralLinksApi';
import { fetchRecruitmentUsersRequest } from '../api/usersApi';

interface ReferralLinkDialogProps {
    open: boolean;
    link: ReferralLink | null;
    onClose: () => void;
    onSave: () => void;
}

interface RecruiterUser {
    id: number;
    username: string;
    firstName?: string;
    lastName?: string;
    role: string;
}

// Mirror of the server-side default so the date picker suggests the
// same value the backend would have applied.
const DEFAULT_EXPIRY_DAYS = 90;

// `<input type="date">` wants YYYY-MM-DD in the user's local calendar — convert
// once here so all reads/writes go through a single shape.
const toDateInputValue = (d: Date): string => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

const defaultExpiryDateString = (): string => {
    const d = new Date();
    d.setDate(d.getDate() + DEFAULT_EXPIRY_DAYS);
    return toDateInputValue(d);
};

const ReferralLinkDialog: React.FC<ReferralLinkDialogProps> = ({ open, link, onClose, onSave }) => {
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        linkName: '',
        recruiterId: null as number | null,
        isActive: true,
        // `hasExpiry === false` maps to expiresAt = null at submit time
        // (opt-out of expiry entirely).
        hasExpiry: true,
        expiresAt: defaultExpiryDateString(),
        metaAdId: '',
        metaCampaignId: '',
    });
    const [recruiters, setRecruiters] = useState<RecruiterUser[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadingRecruiters, setLoadingRecruiters] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [generatedLinkName, setGeneratedLinkName] = useState('');

    useEffect(() => {
        if (open) {
            loadRecruiters();
            if (link) {
                setFormData({
                    title: link.title,
                    description: link.description || '',
                    linkName: link.linkName,
                    recruiterId: link.recruiterId || null,
                    isActive: link.isActive,
                    hasExpiry: link.expiresAt !== null && link.expiresAt !== undefined,
                    expiresAt: link.expiresAt
                        ? toDateInputValue(new Date(link.expiresAt))
                        : defaultExpiryDateString(),
                    metaAdId: link.metaAdId || '',
                    metaCampaignId: link.metaCampaignId || '',
                });
                setGeneratedLinkName('');
            } else {
                setFormData({
                    title: '',
                    description: '',
                    linkName: '',
                    recruiterId: null,
                    isActive: true,
                    hasExpiry: true,
                    expiresAt: defaultExpiryDateString(),
                    metaAdId: '',
                    metaCampaignId: '',
                });
                setGeneratedLinkName('');
            }
            setErrors({});
        }
    }, [open, link]);

    useEffect(() => {
        if (!link && formData.title && !formData.linkName) {
            const generated = formData.title
                .toLowerCase()
                .replace(/[^a-z0-9\s-]/g, '')
                .replace(/\s+/g, '-')
                .replace(/-+/g, '-')
                .replace(/^-|-$/g, '')
                .substring(0, 50);
            setGeneratedLinkName(generated);
        }
    }, [formData.title, formData.linkName, link]);

    const loadRecruiters = async () => {
        try {
            setLoadingRecruiters(true);
            const users = await fetchRecruitmentUsersRequest();
            setRecruiters(users);
        } catch (error) {
            console.error('Failed to load recruiters:', error);
        } finally {
            setLoadingRecruiters(false);
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.title.trim()) {
            newErrors.title = 'Title is required';
        }

        if (!link && formData.linkName) {
            if (!/^[a-z0-9-]+$/.test(formData.linkName)) {
                newErrors.linkName = 'Link name must contain only lowercase letters, numbers, and hyphens';
            }
            if (formData.linkName.length < 3) {
                newErrors.linkName = 'Link name must be at least 3 characters';
            }
            if (formData.linkName.length > 100) {
                newErrors.linkName = 'Link name must be less than 100 characters';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        try {
            setLoading(true);

            // Map UI's hasExpiry switch + date input back to the API's tri-state:
            //   undefined  → "use server default" (only meaningful on create)
            //   null       → "no expiry"
            //   ISO string → explicit override
            // On edit we always send an explicit value so updates can clear or
            // change the expiry; on create we send `null` when the user opted
            // out, otherwise the ISO override (defaulted to today+90).
            let expiresAtPayload: string | null | undefined;
            if (!formData.hasExpiry) {
                expiresAtPayload = null;
            } else if (formData.expiresAt) {
                expiresAtPayload = new Date(formData.expiresAt).toISOString();
            } else {
                expiresAtPayload = link ? null : undefined;
            }

            // Trimmed Meta-ad fields. Empty string → `null` to explicitly
            // clear an existing value on edit; on create the backend treats
            // null and undefined the same (column stays null).
            const metaAdIdTrimmed = formData.metaAdId.trim();
            const metaCampaignIdTrimmed = formData.metaCampaignId.trim();

            const dataToSubmit = {
                title: formData.title.trim(),
                description: formData.description.trim() || undefined,
                recruiterId: formData.recruiterId || undefined,
                isActive: formData.isActive,
                expiresAt: expiresAtPayload,
                metaAdId: metaAdIdTrimmed.length > 0 ? metaAdIdTrimmed : null,
                metaCampaignId: metaCampaignIdTrimmed.length > 0 ? metaCampaignIdTrimmed : null,
            };

            if (link) {
                await updateReferralLinkRequest({
                    id: link.id,
                    ...dataToSubmit,
                });
            } else {
                await createReferralLinkRequest({
                    ...dataToSubmit,
                    linkName: formData.linkName.trim() || undefined,
                });
            }

            onSave();
        } catch (error: any) {
            console.error('Failed to save referral link:', error);
            const serverMessage: string | undefined = error.response?.data?.message;
            if (serverMessage) {
                // Collision check on metaAdId / metaCampaignId throws a 409
                // ConflictException — surface against the right field so the
                // recruiter sees the inline error in context.
                if (error.response?.status === 409 && serverMessage.includes('Meta Ad ID')) {
                    setErrors({ metaAdId: serverMessage });
                } else if (error.response?.status === 409 && serverMessage.includes('Meta Campaign ID')) {
                    setErrors({ metaCampaignId: serverMessage });
                } else if (serverMessage.includes('link name')) {
                    setErrors({ linkName: serverMessage });
                } else {
                    setErrors({ general: serverMessage });
                }
            } else {
                setErrors({ general: 'Failed to save referral link. Please try again.' });
            }
        } finally {
            setLoading(false);
        }
    };

    const getRecruiterDisplayName = (recruiter: RecruiterUser) => {
        const fullName = `${recruiter.firstName || ''} ${recruiter.lastName || ''}`.trim();
        return fullName || recruiter.username;
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {link ? 'Edit Referral Link' : 'Create New Referral Link'}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {errors.general && (
                        <Alert severity="error">{errors.general}</Alert>
                    )}

                    <TextField
                        label="Title"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        fullWidth
                        required
                        error={!!errors.title}
                        helperText={errors.title}
                    />

                    <TextField
                        label="Description"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        fullWidth
                        multiline
                        rows={3}
                    />

                    {!link && (
                        <>
                            <TextField
                                label="Link Name"
                                value={formData.linkName}
                                onChange={(e) => setFormData({ ...formData, linkName: e.target.value })}
                                fullWidth
                                error={!!errors.linkName}
                                helperText={errors.linkName || 'Leave empty to auto-generate from title'}
                                placeholder={generatedLinkName}
                            />
                            {generatedLinkName && !formData.linkName && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Will be generated as:
                                    </Typography>
                                    <Chip
                                        label={generatedLinkName}
                                        size="small"
                                        variant="outlined"
                                    />
                                </Box>
                            )}
                        </>
                    )}

                    {link && link.code && (
                        <TextField
                            label="Attribution Code"
                            value={link.code}
                            fullWidth
                            InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontWeight: 600 } }}
                            helperText="Shared via WhatsApp prefill (#REF…). The code is fixed for the lifetime of the link — disable the link instead if you need to retire it."
                        />
                    )}

                    <FormControl fullWidth>
                        <InputLabel>Recruiter (Optional)</InputLabel>
                        <Select
                            value={formData.recruiterId || ''}
                            onChange={(e) => setFormData({ ...formData, recruiterId: e.target.value ? Number(e.target.value) : null })}
                            label="Recruiter (Optional)"
                            disabled={loadingRecruiters}
                        >
                            <MenuItem value="">
                                <em>None</em>
                            </MenuItem>
                            {recruiters.map((recruiter) => (
                                <MenuItem key={recruiter.id} value={recruiter.id}>
                                    {getRecruiterDisplayName(recruiter)}
                                    <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                                        ({recruiter.role})
                                    </Typography>
                                </MenuItem>
                            ))}
                        </Select>
                        <FormHelperText>
                            Select a recruiter to assign to this referral link
                        </FormHelperText>
                    </FormControl>

                    <FormControlLabel
                        control={
                            <Switch
                                checked={formData.isActive}
                                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                            />
                        }
                        label="Active"
                    />

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.hasExpiry}
                                    onChange={(e) =>
                                        setFormData({ ...formData, hasExpiry: e.target.checked })
                                    }
                                />
                            }
                            label="Has expiry"
                        />
                        <TextField
                            label="Expires on"
                            type="date"
                            value={formData.expiresAt}
                            onChange={(e) =>
                                setFormData({ ...formData, expiresAt: e.target.value })
                            }
                            disabled={!formData.hasExpiry}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            helperText={
                                formData.hasExpiry
                                    ? `Defaults to ${DEFAULT_EXPIRY_DAYS} days from today if left unchanged.`
                                    : 'Link will never expire. Use the Active toggle to retire it manually.'
                            }
                        />
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                            Meta ad linking (optional)
                        </Typography>
                        <TextField
                            label="Meta Ad ID"
                            value={formData.metaAdId}
                            onChange={(e) => setFormData({ ...formData, metaAdId: e.target.value })}
                            fullWidth
                            error={!!errors.metaAdId}
                            helperText={
                                errors.metaAdId ||
                                'If this link is for a specific Meta ad, paste the Ad ID from Ads Manager so CTWA clicks auto-attribute.'
                            }
                        />
                        <TextField
                            label="Meta Campaign ID"
                            value={formData.metaCampaignId}
                            onChange={(e) => setFormData({ ...formData, metaCampaignId: e.target.value })}
                            fullWidth
                            error={!!errors.metaCampaignId}
                            helperText={
                                errors.metaCampaignId ||
                                'For broader campaign-level attribution. Ad ID wins if both are set.'
                            }
                        />
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button 
                    onClick={handleSubmit} 
                    variant="contained" 
                    disabled={loading}
                >
                    {loading ? <CircularProgress size={24} /> : link ? 'Update' : 'Create'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ReferralLinkDialog;